import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

function ProjectsIcon() {
  return (
    <span aria-hidden="true" className="projects-nav-icon grid h-4 w-4 shrink-0 grid-cols-2 gap-[2px]">
      <span className="projects-nav-icon-tile rounded-[2px] bg-[#4CB1A1]" />
      <span className="projects-nav-icon-tile rounded-[2px] bg-[#2dd4bf]" />
      <span className="projects-nav-icon-tile rounded-[2px] bg-[#2dd4bf]" />
      <span className="projects-nav-icon-tile rounded-[2px] bg-[#4CB1A1]" />
    </span>
  );
}

function LangToggle({ lang, setLang, label }) {
  const handleKey = (e) => {
    if (!["ArrowLeft", "ArrowRight", " ", "Enter"].includes(e.key)) return;
    e.preventDefault();
    if (e.key === "ArrowRight") setLang("en");
    else if (e.key === "ArrowLeft") setLang("pt");
    else setLang(lang === "pt" ? "en" : "pt");
  };

  return (
    <div
      role="group"
      aria-label={label}
      onKeyDown={handleKey}
      className="lang-toggle relative inline-grid grid-cols-2 items-center rounded-full h-10 min-w-[110px] px-1 bg-[#F3F5F6] select-none"
    >
      <div
        aria-hidden="true"
        className={[
          "lang-toggle-thumb absolute top-1 left-1 h-8 w-[calc(50%-4px)] rounded-full transition-transform duration-200",
          "bg-[#4CB1A1]",
          lang === "en" ? "translate-x-full" : "translate-x-0",
        ].join(" ")}
      />
      <button
        type="button"
        aria-pressed={lang === "pt"}
        onClick={() => setLang("pt")}
        className={[
          "lang-toggle-option relative z-10 h-8 text-[16px] font-semibold rounded-full outline-none",
          lang === "pt" ? "lang-toggle-option-active" : "",
          lang === "pt" ? "text-white" : "text-[#111]",
          "focus-visible:ring-2 focus-visible:ring-[#4CB1A1] focus-visible:ring-offset-2",
        ].join(" ")}
      >
        PT
      </button>
      <button
        type="button"
        aria-pressed={lang === "en"}
        onClick={() => setLang("en")}
        className={[
          "lang-toggle-option relative z-10 h-8 text-[16px] font-semibold rounded-full outline-none",
          lang === "en" ? "lang-toggle-option-active" : "",
          lang === "en" ? "text-white" : "text-[#111]",
          "focus-visible:ring-2 focus-visible:ring-[#4CB1A1] focus-visible:ring-offset-2",
        ].join(" ")}
      >
        EN
      </button>
    </div>
  );
}

export default function Navbar({ activeLang = "pt", onLanguageChange }) {
  const [lang, setLang] = useState(activeLang);
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setLang(activeLang);
  }, [activeLang]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const updateLang = (valueOrUpdater) => {
    setLang((prev) => {
      const next = typeof valueOrUpdater === "function" ? valueOrUpdater(prev) : valueOrUpdater;
      if (typeof onLanguageChange === "function" && next !== prev) {
        onLanguageChange(next);
      } else if (typeof valueOrUpdater !== "function" && next === prev && typeof onLanguageChange === "function") {
        // if same value passed directly, still notify parent to ensure sync
        onLanguageChange(next);
      }
      return next;
    });
  };

  const t = {
    pt: {
      about: "Sobre Nós",
      projects: "Projetos",
      contact: "Contactos",
      podcast: "Podcast",
      toggleLabel: "Selecionar idioma",
    },
    en: {
      about: "About Us",
      projects: "Projects",
      contact: "Contact",
      podcast: "Podcast",
      toggleLabel: "Select language",
    },
  };
  const basePath = lang === "en" ? "/en" : lang === "pt" ? "/pt" : "/";
  const normalizedBasePath = basePath === "/" ? "" : basePath;
  const sectionHref = (hash) => `${basePath}${hash}`;
  const projectsHref = "/projetos";
  const podcastHref = `${normalizedBasePath}/podcast`;
  const isOnProjectsPage = location.pathname.toLowerCase().includes("/projetos");
  const isOnPodcastPage = location.pathname.toLowerCase().includes("/podcast");
  const projectsLinkClasses = [
    "group relative inline-flex items-center gap-2 text-[16px] font-medium tracking-[0.2px]",
    isOnProjectsPage ? "text-[#4CB1A1] nav-link-active" : "text-[#111] nav-link",
    "hover:text-[#4CB1A1]",
  ].join(" ");
  const baseHeaderClass = "fixed top-0 left-0 w-full z-50 backdrop-blur-sm transition-colors duration-300";

  const scrolledClass = "navbar-scrolled border-b border-slate-200/80 shadow-[0_6px_18px_-12px_rgba(30,42,90,0.3)]";

  const unscrolledClass = "navbar-initial border-b border-transparent shadow-none";

  const routeClass = location.pathname.toLowerCase().includes("/prr") ? "navbar-prr" : "navbar-home";

  return (
    <header
      className={["theme-navbar", baseHeaderClass, routeClass, scrolled ? scrolledClass : unscrolledClass].join(" ")} 
    >
<nav className="mx-auto max-w-[1900px] px-2 sm:px-4 md:px-8 lg:px-12">
        <div className="h-[88px] flex items-center justify-between">

          <a href={basePath} aria-label="Invisible Cloud - Início" className="inline-block leading-none ml-4 md:ml-8 lg:ml-12">
            <picture>
              <source media="(prefers-color-scheme: dark)" srcSet="/assets/logoWhite.png" />
              <img src="/assets/logo.png" alt="Invisible Cloud" className="h-[30px] w-auto" />
            </picture>
          </a>

          {/* Desktop: */}
          <div className="hidden md:flex items-center gap-10 lg:gap-7">
            <a
              href={sectionHref("#about")}
              className="nav-link text-[16px] font-medium tracking-[0.2px] text-[#111] hover:text-[#4CB1A1]"
            >
              {t[lang].about}
            </a>
       
            <a
              href={sectionHref("#footer")}
              className="nav-link text-[16px] font-medium tracking-[0.2px] text-[#111] hover:text-[#4CB1A1]"
            >
              {t[lang].contact}
            </a>

            <a href={projectsHref} className={projectsLinkClasses}>
              {!isOnProjectsPage && <ProjectsIcon />}
              <span>{t[lang].projects}</span>
            </a>

            <a
              href={podcastHref}
              className={[
                "group relative inline-flex items-center gap-2 text-[16px] font-medium tracking-[0.2px]",
                isOnPodcastPage ? "text-[#4CB1A1] nav-link-active" : "text-[#111] nav-link",
                "hover:text-[#4CB1A1]",
              ].join(" ")}
            >
              {!isOnPodcastPage && (
                <span className="inline-flex items-end gap-[2px] h-4 w-4">
                  <span className="w-[3px] rounded-full bg-[#4CB1A1] animate-[wave-bar_1s_ease-in-out_infinite]" style={{ height: "40%" }} />
                  <span className="w-[3px] rounded-full bg-[#2dd4bf] animate-[wave-bar_1s_ease-in-out_0.2s_infinite]" style={{ height: "70%" }} />
                  <span className="w-[3px] rounded-full bg-[#4CB1A1] animate-[wave-bar_1s_ease-in-out_0.4s_infinite]" style={{ height: "100%" }} />
                  <span className="w-[3px] rounded-full bg-[#2dd4bf] animate-[wave-bar_1s_ease-in-out_0.6s_infinite]" style={{ height: "55%" }} />
                </span>
              )}
              {t[lang].podcast}
            </a>

            <LangToggle lang={lang} setLang={updateLang} label={t[lang].toggleLabel} />
          </div>

          {/* Mobile: botão hambúrguer à direita */}
          <button
            type="button"
            aria-label="Abrir menu"
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen((v) => !v)}
            className="nav-menu-button md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-slate-200 hover:bg-slate-50"
          >
            <span className="sr-only">Menu</span>
            {/* ícone hambúrguer / close */}
            <svg
              className={`h-5 w-5 ${open ? "hidden" : "block"}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg
              className={`h-5 w-5 ${open ? "block" : "hidden"}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M6 6l12 12M6 18L18 6" />
            </svg>
          </button>
        </div>

        {/* Painel mobile */}
        <div
          id="mobile-menu"
          className={[
            "md:hidden overflow-hidden transition-[max-height] duration-300",
            open ? "max-h-96" : "max-h-0",
          ].join(" ")}
        >
          <div className="mobile-menu-panel pb-4 pt-2 border-t border-slate-200">
            <div className="mobile-menu-panel flex flex-col gap-3 items-end">
              <a
                href={sectionHref("#about")}
                className="mobile-nav-link text-[16px] font-medium tracking-[0.2px] text-[#111] hover:text-[#4CB1A1]"
                onClick={() => setOpen(false)}
              >
                {t[lang].about}
              </a>
              
              <a
                href={podcastHref}
                className={[
                  "group relative inline-flex items-center gap-2 text-[16px] font-medium tracking-[0.2px]",
                  isOnPodcastPage ? "text-[#4CB1A1] nav-link-active" : "text-[#111] mobile-nav-link",
                  "hover:text-[#4CB1A1]",
                ].join(" ")}
                onClick={() => setOpen(false)}
              >
                {!isOnPodcastPage && (
                  <span className="inline-flex items-end gap-[2px] h-4 w-4">
                    <span className="w-[3px] rounded-full bg-[#4CB1A1] animate-[wave-bar_1s_ease-in-out_infinite]" style={{ height: "40%" }} />
                    <span className="w-[3px] rounded-full bg-[#2dd4bf] animate-[wave-bar_1s_ease-in-out_0.2s_infinite]" style={{ height: "70%" }} />
                    <span className="w-[3px] rounded-full bg-[#4CB1A1] animate-[wave-bar_1s_ease-in-out_0.4s_infinite]" style={{ height: "100%" }} />
                    <span className="w-[3px] rounded-full bg-[#2dd4bf] animate-[wave-bar_1s_ease-in-out_0.6s_infinite]" style={{ height: "55%" }} />
                  </span>
                )}
                {t[lang].podcast}
              </a>
              <a href={projectsHref} className={projectsLinkClasses} onClick={() => setOpen(false)}>
                {!isOnProjectsPage && <ProjectsIcon />}
                <span>{t[lang].projects}</span>
              </a>
              <a
                href={sectionHref("#footer")}
                className="mobile-nav-link text-[16px] font-medium tracking-[0.2px] text-[#111] hover:text-[#4CB1A1]"
                onClick={() => setOpen(false)}
              >
                {t[lang].contact}
              </a>
              <LangToggle lang={lang} setLang={updateLang} label={t[lang].toggleLabel} />
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}
